import { useEffect } from 'react';
import { useAppSelector } from 'app/store';
import { PAIDOL_SESSION_ID_KEY } from 'app/store/authSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { SnackbarUtils } from 'app/shared-components/util/SnackbarUtils';

const RELOAD_DELAY_MS = 1500;

/**
 * SEE-1583: follow a company switch made in another tab.
 *
 * Tabs of one browser share localStorage, so when one tab switches company the
 * session key, the Cognito tokens and the server-side session row all move to
 * the new company while every other tab keeps its old company in Redux. From
 * then on each of those tabs reads a company its token no longer grants: every
 * paidol-scoped request is refused, the session check passes (the key and the
 * row agree), and the page freezes on whatever rows it had. That is what the
 * customer saw as "the filter shows but the table never changes".
 *
 * The `storage` event fires only in the OTHER tabs, which is exactly the set
 * that has to catch up. Reload them into the newly selected company.
 */
export function useCrossTabPaidolSync(): void {
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PAIDOL_SESSION_ID_KEY || !event.newValue) return;
      const [nextPaidolId] = event.newValue.split(':');
      if (!nextPaidolId || !selectedPaidolId || nextPaidolId === selectedPaidolId) return;
      SnackbarUtils.enqueue('Your selected company was changed in another tab. Reloading…', {
        variant: 'info',
        autoHideDuration: RELOAD_DELAY_MS,
      });
      setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [selectedPaidolId]);
}
